import React, { useState, useEffect, useRef } from 'react'
// import CancelIcon from '@mui/icons-material/Cancel';
import {Cancel as CancelIcon, ChevronLeft, ChevronRight, Warning as WarningIcon} from '@mui/icons-material';
import { CredentialSchema, CurrentRequirementsSchema, ResourceSchema, ResourceType, ResourceBookingSchema  } from "../../data/types";
import { foodBusinessCategories } from '../../utils/enums';
import { capitalizeString, capitalizeWords, getDirectLinkFromGoogleDriveSharedLink, styleThousandCommas } from '../../utils/helpers';
import StripePaymentCheckOut from './PaymentCheckOut';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useCompleteSignIn, useLoadUserResourceBooking } from '../../hooks/authhooks';
import { ReactComponent as CheckMarkIcon } from "../../icons/CheckMarkIcon.svg";
import SubmitRequestButton from '../../components/SubmitRequestButton';
// import { ReactComponent as WarningIcon } from "../icons/WarningIcon.svg";
// import { ReactComponent as WalletIcon } from "../icons/WalletIcon.svg";


type PhotoPreviewContentProps = {
  isOpen: boolean
  photos: string[]
  currentIndex: number
  resourceName: string
  onClose: () => void
  onNavigate: (index: number) => void
}

export const PhotoPreviewContent: React.FC<PhotoPreviewContentProps> = ({ isOpen, photos, currentIndex, resourceName, onClose, onNavigate }) => {
  const getPhotoUrl = (photo: string) => {
    return getDirectLinkFromGoogleDriveSharedLink(photo) || ''
  }

  const validPhotos = photos && photos.length > 0 ? photos.filter(p => getPhotoUrl(p)) : []
  const currentPhoto = validPhotos[currentIndex]
  const currentPhotoUrl = currentPhoto ? getPhotoUrl(currentPhoto) : ''

  useEffect(() => {
    if (!isOpen || validPhotos.length === 0) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : validPhotos.length - 1
        onNavigate(newIndex)
      }
      if (e.key === 'ArrowRight') {
        const newIndex = currentIndex < validPhotos.length - 1 ? currentIndex + 1 : 0
        onNavigate(newIndex)
      }
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, currentIndex, validPhotos.length, onNavigate, onClose])

  if (!isOpen || validPhotos.length === 0) return null

  return (
    <>
      {/* Previous button */}
      {validPhotos.length > 1 && (
        <button
          onClick={() => {
            const newIndex = currentIndex > 0 ? currentIndex - 1 : validPhotos.length - 1
            onNavigate(newIndex)
          }}
          className='absolute left-4 text-white hover:text-gray-300 z-50 bg-black/50 rounded-full p-2'
        >
          <ChevronLeft style={{ fontSize: 40 }} />
        </button>
      )}

      {/* Photo */}
      <div className='max-w-7xl max-h-[90vh] flex items-center justify-center'>
        {currentPhotoUrl ? (
          <img
            className='max-w-full max-h-[90vh] object-contain rounded-lg'
            src={currentPhotoUrl}
            alt={`${resourceName} ${currentIndex + 1}`}
          />
        ) : (
          <div className='text-white text-xl'>Image not available</div>
        )}
      </div>

      {/* Next button */}
      {validPhotos.length > 1 && (
        <button
          onClick={() => {
            const newIndex = currentIndex < validPhotos.length - 1 ? currentIndex + 1 : 0
            onNavigate(newIndex)
          }}
          className='absolute right-4 text-white hover:text-gray-300 z-50 bg-black/50 rounded-full p-2'
        >
          <ChevronRight style={{ fontSize: 40 }} />
        </button>
      )}

      {/* Photo counter */}
      {validPhotos.length > 1 && (
        <div className='absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full'>
          {currentIndex + 1} / {validPhotos.length}
        </div>
      )}
    </>
  )
}

type DeleteDataItemType = {
  id: string
  item_name: string
  date?: string
  name?: string
}

type DeleteConfirmationFormProps = {
  dataName: string
  data: DeleteDataItemType
  handleClose: () => void;
  handleDelete: (dataId: string) => void;
};

export const DeleteConfirmationForm: React.FC<DeleteConfirmationFormProps> = (props) => {
  let { dataName, data, handleClose, handleDelete } = props;

  useEffect(() => {}, [dataName, data]);

  var deletionItemName = ''
  if (dataName === 'resource' || data?.name) {
    deletionItemName = data?.name ? ` ${data.name}` : ''
  } else if (data?.item_name || data?.date) {
    deletionItemName = data?.item_name ? ` ${data.item_name}` : ((data?.date) ? ` ${moment(data.date).format('ddd MMM DD, YYYY')}` : '')
  }

  return (
    <div className="flex flex-col container w-full">
      <h1 className="text-[20px] text-black font-semibold font-raleway">
        Delete {dataName ? capitalizeWords(dataName) : 'Item'}?
      </h1>
      <div className="text-[16px] w-full text-[#333333] my-2 font-open-sans">
        Are you sure you want to delete the {dataName ? dataName.toLowerCase(): 'item'}:<b>{deletionItemName}</b>?
      </div>
      <div className="text-[14px] w-full text-[#777777] my-2 font-open-sans">
        <WarningIcon className='w-4 h-4 my-auto' fontSize='small' /> <span className='my-auto ml-1'>This action is permanent and cannot be undone.</span>
      </div>
      <div className="flex flex-row justify-between w-full gap-x-1 my-2">
        <button
          className="w-1/2 font-raleway font-semibold text-center text-[#333333] bg-white border-[#E9E9E9] border-[1px] rounded "
          onClick={handleClose}
        >
          No, Cancel
        </button>
        <button
          className="w-1/2 h-10 border-0 text-center text-white bg-[#D42620] rounded-md font-raleway font-semibold"
          onClick={()=>handleDelete(data.id)}
          data-testid="delete-button"
        >
          Yes, Confirm
        </button>
      </div>
    </div>
  );
};


type AddPaymentMethodFormProps = {
    loggedinUid: string;
    isAddingPayMethod: boolean;
    setIsAddingPayMethod: React.Dispatch<React.SetStateAction<boolean>>;
    handleClose: () => void;
    // handleSubmitPaymentMethod: (paymentMethod: PaymentMethod) => void;
};
  
export const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = (props) => {
    var { loggedinUid, isAddingPayMethod, setIsAddingPayMethod } = props;
  
    return (
      <div className='flex flex-col w-full'>
        <p className='text-[18px] font-raleway text-black font-semibold'>Add Your Payment Method</p>
        <p className='text-[14px] font-open-sans text-[#808080] mt-2 mb-4'>
          Choose your payment method from the available options below.
        </p>
        
        {/* <div className='flex flex-row rounded-md mb-7 sm:h-[40px] w-[90px] bg-primaryDark-0 text-[14px] text-white font-bold font-raleway'>
          <WalletIcon className='my-auto ml-auto mr-1' />
          <p className='my-auto mr-auto'>Card</p>
        </div> */}

        <StripePaymentCheckOut uid={loggedinUid} mode="setup" currency="usd" setup_future_usage='off_session' isAddPayMethod={isAddingPayMethod} setIsAddingPayMethod={setIsAddingPayMethod} />
      </div>
    );
};

type AddCredentialFormProps = {
    requirement: CurrentRequirementsSchema
    handleClose: () => void;
    handleSubmitCredential: (credential: CredentialSchema, communityId: string) => void;
};
  
export const AddCredentialForm: React.FC<AddCredentialFormProps> = (props) => {
    var { requirement, handleSubmitCredential, handleClose } = props;
  
    const [credential, setCredential] = useState<CredentialSchema>({} as CredentialSchema);
    const fileInputRef = useRef<HTMLInputElement>(null);
  
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, files } = event.target;
      if (name && files && files.length > 0) {
        let file_data = files[0];
        setCredential(({
          category: (requirement && requirement.code) ? requirement.code : "",
          [name]: file_data,
        }));
      }
    };

  
    const handleSubmit = () => {
      if (credential && requirement?.community_id) {
        credential.community_id = requirement.community_id
        // console.log("credential", credential)
        handleSubmitCredential(credential, requirement.community_id);
        setCredential({} as CredentialSchema);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        handleClose();
      }
    };

    // console.log("credential", credential)
  
    return (
      <div className="flex flex-col justify-evenly items-center h-full">
        <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
            <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
        </span>

        <div className="flex flex-col w-full mx-auto mt-6">
          <h1 className="text-[18px] font-bold font-raleway text-primaryDark-200/90 text-left">{requirement?.is_required && requirement?.presigned_url ? 'Update' : 'Upload'} <span className='font-semibold'>{(requirement && requirement?.name) ? requirement.name : 'Credential'}</span></h1>
          <h1 className="text-[12px] font-open-sans text-base text-[#333333] text-left my-2">{(requirement && requirement?.description)  ? requirement.description : ""}</h1>
        </div>
       
        <div className="flex flex-col mx-auto w-full my-4">
            <label className="text-black text-left my-2 font-raleway font-medium">Upload</label>
            <div className='border-[#E9E9E9] border-[1px] rounded-md'>
                <input
                    ref={fileInputRef}
                    type="file"
                    placeholder="Upload File"
                    className="formInput m-auto w-[430px] h-[59px] mx-auto bg-inherit p-3 border-0"
                    name="file"
                    onChange={handleFileChange}
                />
            </div>
        </div>
        <button
            className="buttonsPrimary h-[54px] w-[430px] mx-auto mt-auto font-bold text-[18px] font-raleway border-0 bg-primaryDark-0 text-white"
            onClick={handleSubmit}
            data-testid="submit-button"> 
                Submit  
        </button>
      </div>
    );
  };






type ApplyFundingFormProps = {
    resourceDetail: ResourceSchema
    handleClose: () => void;
    handleSubmitRequest: (resource: ResourceSchema, resourceType: ResourceType, booking: ResourceBookingSchema) => Promise<boolean> | boolean | void
};
  
export const ApplyFundingForm: React.FC<ApplyFundingFormProps> = (props) => {
    var { resourceDetail, handleSubmitRequest, handleClose } = props;
    const resourceType = resourceDetail?.resource_type
    const navigate = useNavigate();
    const { user } = useCompleteSignIn();
    const loggedinUid = (user && user?.uid) ? user.uid : ''
    const { userResourceBooking } = useLoadUserResourceBooking(resourceDetail?.community_id || "", resourceDetail?.id || "", loggedinUid);
  
    const [application, setApplication] = useState<ResourceBookingSchema>({} as ResourceBookingSchema);
    const [businessCategory, setBusinessCategory] = useState<number>(0);
    const [businessTypes, setBusinessTypes] = useState<string[]>(foodBusinessCategories[businessCategory].types);
    const [businessType, setbusinessType] = useState<string>(businessTypes[0]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
  
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name && value) {
        setApplication((prev: ResourceBookingSchema) => ({
          ...prev,
          [name]: value
        }));
      }
    };
    const selectBusinessCategories = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.preventDefault()
      const {name, value} = e.target
      if (name === 'category' && value){
        const categoryIndex = parseInt(value)
        const categoryObject = foodBusinessCategories[categoryIndex]
        setBusinessCategory(categoryIndex)
        setApplication((prev: ResourceBookingSchema) => ({
          ...prev,
          business_category: categoryObject.category,
        }));
        setBusinessTypes(categoryObject.types)
      } else if (name === 'business_type' && value){
        setbusinessType(value)
        setApplication((prev: ResourceBookingSchema) => ({
          ...prev,
          business_type: value,
        }));
      }
      
    }
  
    const handleSubmit = async () => {
      if (isSubmitting || !(application && resourceDetail && resourceDetail.resource_type)) return;
      const total_cost = resourceDetail.total_amount ? resourceDetail.total_amount : 0
      application.total_cost = total_cost
      setIsSubmitting(true);
      try {
        await handleSubmitRequest(resourceDetail, resourceDetail.resource_type, application);
        handleClose();
        navigate('/bookings');
      } finally {
        setIsSubmitting(false);
      }
    };
  
    return (
      <div className="flex flex-col justify-evenly items-center h-full">
        <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
            <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
        </span>

        <div className='h-[4px] border-0 bg-primaryDark-400 w-11/12 my-4' />
        
        {(userResourceBooking && userResourceBooking?.status && userResourceBooking?.status !== 'canceled') ? (
          <div className="flex flex-col items-center justify-center w-11/12 py-8">
            <div className="w-16 h-16 mb-6 rounded-full bg-primaryDark-400 flex items-center justify-center">
              <CheckMarkIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className='text-black font-semibold text-[24px] font-raleway text-center mb-2'>
              Request Already Submitted
            </h2>
            <p className='text-[#4D4D4D] text-[16px] font-open-sans text-center mb-6 max-w-md'>
              You have already submitted a request for <span className='font-semibold'>{resourceDetail.name}</span> on <span className='font-semibold'>{moment(userResourceBooking?.created).format('MMM DD, YYYY')}</span>. Please check your bookings for status updates.
            </p>
            <button
              onClick={() => {
                handleClose();
                navigate('/bookings');
              }}
              className="py-2 px-8 font-bold text-[18px] font-raleway border-0 bg-primaryDark-0 text-white rounded-lg hover:bg-primaryDark-300 transition-colors"
            >
              View My Bookings
            </button>
          </div>
        ) : (
          <>
            <p className='text-black font-medium text-[24px] font-raleway text-left w-11/12'>
              Want to Apply to <span className='font-bold'>{resourceDetail.name}</span>?
            </p>
            <p className="text-left text-[16px] my-2 font-open-sans font-medium text-[#333333] w-11/12">Fill out the details below to get support with funding for your business.</p>

            <div className='border-[1px] border-[#E9E9E9] p-1 rounded-lg w-11/12'>
          <div className="flex flex-col mx-auto w-11/12 my-4">
              <label className='text-black font-semibold text-[16px] font-raleway text-left'>
                Business Category
              </label>
              <div className='border-[#E9E9E9] border-[1px] rounded-md my-2'>
                  <select
                      className="formInput m-auto w-full h-[59px] mx-auto bg-inherit p-3 border-0"
                      name="category"
                      value={businessCategory}
                      onChange={e => {
                        selectBusinessCategories(e)
                      }}
    
                  >

                    {
                      foodBusinessCategories.map((data: any, i: number)=>
                        (
                          <option value={i} key={i}>
                              {data.category}
                          </option>
                        )
                      )
                    }
                  </select>
              </div>
          </div>

          <div className="flex flex-col mx-auto w-11/12 my-4">
              <label className='text-black font-semibold text-[16px] font-raleway text-left'>
                Type of Business
              </label>
              <div className='border-[#E9E9E9] border-[1px] rounded-md my-2'>
                  <select
                      className="formInput m-auto w-full h-[59px] mx-auto bg-inherit p-3 border-0"
                      name="business_type"
                      value={businessType}
                      onChange={selectBusinessCategories}
                  >

                    {
                      businessTypes.map((type: any, i: number)=>
                      (
                        <option value={type} key={i}>
                            {type}
                        </option>
                      ))
                    }
                  </select>
              </div>
          </div>

          <div className="flex flex-col mx-auto w-11/12 my-4">
              <label className='text-black font-semibold text-[16px] font-raleway text-left'>
                Business Name
              </label>
              <div className='border-[#E9E9E9] border-[1px] rounded-md my-2'>
                  <input
                      type="text"
                      placeholder="Business Name"
                      className="formInput m-auto w-full h-[59px] mx-auto bg-inherit p-3 border-0"
                      name="business_name"
                      value={application?.business_name || ''}
                      onChange={handleFileChange}
                  />
              </div>
          </div>


          <div className="flex flex-col mx-auto w-11/12 my-4">
              <label className='text-black font-semibold text-[16px] font-raleway text-left'>
                Address
              </label>
              <div className='border-[#E9E9E9] border-[1px] rounded-md my-2'>
                  <input
                      type="text"
                      placeholder="Business Address"
                      className="formInput m-auto w-full h-[59px] mx-auto bg-inherit p-3 border-0"
                      name="location"
                      value={application?.location || ''}
                      onChange={handleFileChange}
                  />
              </div>
          </div>

          <div className="flex flex-row rounded-lg h-auto mx-auto w-11/12 my-2 text-black font-semibold text-[16px] font-raleway text-left">
            <p>{resourceType ? capitalizeString(resourceType) : 'Resource'} amount:</p>
            <p className='font-normal ml-1 text-[15px]'>&nbsp;{resourceDetail?.total_amount ? `$${styleThousandCommas(resourceDetail.total_amount.toFixed(2))}` : 'Not Specified'}</p>
          </div>
        </div>
        <div className='flex flex-row my-2 w-11/12'>
          <button
              className="py-2 w-[217px] mr-auto font-bold text-[18px] font-raleway text-primaryDark-0 border-[#E9E9E9] border-[1px]"
              data-testid="cancel-button"
              onClick={handleClose}
            > 
                  Cancel  
          </button>
          <SubmitRequestButton
              isSubmitting={isSubmitting}
              disabled={(!businessCategory && businessCategory !== 0) || !businessType || !application?.business_name}
              onClick={handleSubmit}
              className={`py-2 w-[217px] ml-auto font-bold text-[18px] font-raleway border-0 text-white ${((!businessCategory && businessCategory !== 0) || !businessType || !application?.business_name) ? 'bg-gray-300' : 'bg-primaryDark-0'}`}
          />
        </div>
          </>
        )}
      </div>
    );
  };

